/**
 * DESIGN TOKENS — Single Source of Truth
 *
 * All color / radius / shadow tokens for the Meester djay design system.
 * Components MUST import from here instead of hardcoding Tailwind color classes.
 *
 * To change the brand color: update ONLY this file + index.css CSS variables.
 *
 * Architecture: DDD-aligned. This is a cross-cutting config concern.
 * Location: src/config/designTokens.ts (alongside constants.ts)
 *
 * ─────────────────────────────────────────────────────────────────────
 * SSOT CONSOLIDATION (LEARNER_UI_COHERENCE_AUDIT_2026 — Fase 0)
 * ─────────────────────────────────────────────────────────────────────
 * Wat in deze ronde is rechtgezet/aangevuld:
 *  1. DANGER is nu écht ROOD (rose) — niet langer amber. Dit lost de
 *     "danger oogt als warning"-val op en aligneert met index.css
 *     `--color-danger: #EF4444`.
 *  2. WARNING is een eigen semantisch token (amber) — losgekoppeld van DANGER.
 *  3. INFO is een eigen top-level token (blue) — niet langer alleen in badges.
 *  4. Elk status-token heeft een AA-veilige `*-text`-variant voor KLEINE
 *     gekleurde tekst/icon/link op licht. Regel: felle 500-fills alleen voor
 *     vlakken met witte tekst; kleine gekleurde tekst gebruikt de `*Text`-variant.
 *  5. RADIUS + SHADOW zijn nu getokeniseerd (was alleen los in index.css) —
 *     dit dwingt de radius-schaal af en stopt de `rounded-[2rem]`/`[2.5rem]`-drift.
 *  6. MASTERY heeft een EIGEN schaal (drempels 75/55/35) en hergebruikt NOOIT
 *     status-kleuren — beheersing ≠ correctheid.
 *  7. Het module-palet (de breedst gebruikte kleurbron) wordt nu vanuit deze
 *     SSOT ge-re-exporteerd, zodat designTokens het aggregatiepunt is.
 *
 * Regels (codificatie van de audit §6.2):
 *  - Map op BETEKENIS, niet op hue (los emerald/amber-collisies semantisch op).
 *  - Fill vs tekst (WCAG AA): 500-accenten = fills; kleine tekst = `*Text`.
 *  - Mastery ≠ status. Module = puur decoratief (nooit correctheid).
 */

// Re-export het module-palet zodat designTokens.ts het SSOT-aggregatiepunt is
// (analoog aan hoe uiHelpers.ts ROLE_COLORS re-exporteert). De canonieke data
// blijft in content/moduleColors.ts (Content-domein), maar consumenten kunnen
// "het design system" als één import-pad behandelen.
export {
  getModuleColors,
  getModuleHexColor,
  getModuleTint,
  lightenHex,
  MODULE_COLOR_MAP,
} from '../content/moduleColors'
export type { ModuleColorSet } from '../content/moduleColors'

// ─── Brand / Primary ────────────────────────────────────────────────
export const PRIMARY = {
  bg: 'bg-orange-500',
  bgHover: 'hover:bg-orange-400',
  bgLight: 'bg-orange-50',
  bgLightHover: 'hover:bg-orange-100',
  bgMuted: 'bg-orange-50/80',
  text: 'text-orange-600',
  textDark: 'text-orange-700',
  textDarker: 'text-orange-900',
  textOnPrimary: 'text-white',
  border: 'border-orange-200',
  borderStrong: 'border-orange-400',
  borderHover: 'hover:border-orange-300',
  ring: 'ring-orange-300',
  ringLight: 'ring-orange-100',
  focusRing: 'focus:ring-2 focus:ring-orange-300',
  focusBorder: 'focus:border-orange-400',
  shadow3d: 'shadow-[0_4px_0_0_rgb(194,65,12)]',
  shadow3dHover: 'hover:shadow-[0_4px_0_0_rgb(194,65,12)]',
  shadowGlow: 'shadow-orange-200/50',
  gradient: 'from-orange-500 via-amber-500 to-yellow-500',
  gradientSubtle: 'from-orange-50 to-amber-50',
} as const

// ─── Outline variant (secondary brand) ─────────────────────────────
export const OUTLINE = {
  bg: 'bg-transparent',
  bgHover: 'hover:bg-orange-50',
  text: 'text-orange-600',
  border: 'border-orange-200',
  borderHover: 'hover:border-orange-300',
  bgActive: 'active:bg-orange-100',
  ring: 'ring-orange-300',
} as const

// ─── Status: Success (correct answers, mastered surfaces) ───────────
export const SUCCESS = {
  bg: 'bg-emerald-500',
  bgLight: 'bg-emerald-50',
  text: 'text-emerald-600',
  /** AA-safe small text/icon/link on light */
  textOnLight: 'text-emerald-700',
  textDark: 'text-emerald-700',
  border: 'border-emerald-200',
  ring: 'ring-emerald-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(5,150,105)]',
  shadow3dHover: 'hover:shadow-[0_4px_0_0_rgb(5,150,105)]',
} as const

// ─── Status: Warning (caution, attention — NOT destructive) ─────────
// Eigen semantisch token. Vroeger zat "warning" verstopt in de DANGER-token
// (amber). Nu expliciet losgekoppeld zodat warning ≠ danger.
export const WARNING = {
  bg: 'bg-amber-500',
  bgHover: 'hover:bg-amber-400',
  bgLight: 'bg-amber-50',
  text: 'text-amber-600',
  /** AA-safe small text/icon/link on light */
  textOnLight: 'text-amber-700',
  textDark: 'text-amber-700',
  border: 'border-amber-200',
  ring: 'ring-amber-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(217,119,6)]',
  shadow3dHover: 'hover:shadow-[0_4px_0_0_rgb(217,119,6)]',
} as const

// ─── Status: Danger (destructive actions, errors — REAL RED) ────────
// CHANGED (Fase 0): was amber (bg-amber-500). Nu echt rood (rose), in lijn
// met index.css `--color-danger: #EF4444`. Enige consument was Button.tsx
// (danger-variant) — die is mee-bijgewerkt.
export const DANGER = {
  bg: 'bg-rose-500',
  bgHover: 'hover:bg-rose-400',
  bgLight: 'bg-rose-50',
  text: 'text-rose-600',
  /** AA-safe small text/icon/link on light */
  textOnLight: 'text-rose-700',
  textDark: 'text-rose-700',
  border: 'border-rose-200',
  ring: 'ring-rose-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(225,29,72)]',
  shadow3dHover: 'hover:shadow-[0_4px_0_0_rgb(225,29,72)]',
} as const

// ─── Status: Info (neutral informational) ───────────────────────────
export const INFO = {
  bg: 'bg-blue-500',
  bgLight: 'bg-blue-50',
  text: 'text-blue-600',
  /** AA-safe small text/icon/link on light */
  textOnLight: 'text-blue-700',
  textDark: 'text-blue-700',
  border: 'border-blue-200',
  ring: 'ring-blue-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(37,99,235)]',
} as const

// ─── XP / Gamification ─────────────────────────────────────────────
export const XP = {
  bg: 'bg-purple-500',
  bgLight: 'bg-purple-50',
  text: 'text-purple-600',
  /** AA-safe small text/icon/link on light */
  textOnLight: 'text-purple-700',
  textDark: 'text-purple-700',
  border: 'border-purple-200',
} as const

// ─── Neutral / Ghost ────────────────────────────────────────────────
export const NEUTRAL = {
  bg: 'bg-white',
  bgHover: 'hover:bg-slate-50',
  bgMuted: 'bg-slate-50',
  text: 'text-slate-600',
  textDark: 'text-slate-700',
  textDarker: 'text-slate-900',
  /** Tertiary / placeholder / disabled text */
  textSubtle: 'text-slate-500',
  border: 'border-slate-200',
  borderStrong: 'border-slate-300',
  borderHover: 'hover:border-slate-300',
  ring: 'ring-slate-300',
  shadow3d: 'shadow-[0_4px_0_0_rgb(226,232,240)]',
} as const

// ─── STATUS map (convenience accessor — semantic lookup) ────────────
// Gebruik dit wanneer een component dynamisch een status-kleur kiest, zodat
// "success" altijd hetzelfde betekent en je nooit rauwe green-500/emerald-500
// door elkaar haalt (audit §5.4: GlanceBar green ≠ SUCCESS emerald).
export const STATUS = {
  success: SUCCESS,
  warning: WARNING,
  danger: DANGER,
  info: INFO,
} as const
export type StatusKey = keyof typeof STATUS

// ─── Role-Based Colors ─────────────────────────────────────────────
// Canonical color palette per account role.
// Single source of truth — uiHelpers.ts MUST re-export from here, never override.
//
// - Teacher: violet (warm, distinct from learner orange & parent emerald)
// - Parent:  emerald
// - Learner: primary orange (brand)
// - Admin:   amber (signals "system" / "management")
//
// LET OP (audit §6.1 amber-overload): admin deelt amber met WARNING. Houd
// admin-identiteit (bg-50/border-200/badge) en warning-status (bg-500 fill)
// visueel uit elkaar — gebruik voor admin nooit de felle amber-500-fill.
//
// F-ACC-CQ-R2-ROLE-COLOR-INCONSISTENCY: all 4 roles defined here; uiHelpers
// re-exports verbatim, no overrides.
export const ROLE_COLORS = {
  teacher: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-600',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
    hoverBorder: 'hover:border-violet-300',
  },
  parent: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
    hoverBorder: 'hover:border-emerald-300',
  },
  learner: {
    bg: PRIMARY.bgLight,
    text: PRIMARY.textDark,
    border: PRIMARY.border,
    iconBg: 'bg-orange-100',
    iconText: PRIMARY.text,
    badgeBg: 'bg-orange-100',
    badgeText: PRIMARY.textDark,
    hoverBorder: PRIMARY.borderHover,
  },
  admin: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    hoverBorder: 'hover:border-amber-300',
  },
} as const

// ─── Mastery scale (EIGEN schaal — hergebruikt NOOIT status) ────────
// Drempels 75 / 55 / 35 (zie mastery-domein). Beheersing ≠ correctheid:
// daarom een aparte schaal i.p.v. SUCCESS/WARNING/DANGER hergebruiken.
//   strong      ≥ 75  (sterk beheerst)
//   solid       ≥ 55  (degelijk, indigo — bewust géén emerald/groen)
//   developing  ≥ 35  (in ontwikkeling)
//   attention   <  35 (aandacht nodig)
export const MASTERY = {
  strong: {
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    ring: '#10B981',
  },
  solid: {
    bg: 'bg-indigo-500',
    bgLight: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    ring: '#6366F1',
  },
  developing: {
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    ring: '#F59E0B',
  },
  attention: {
    bg: 'bg-rose-500',
    bgLight: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    ring: '#F43F5E',
  },
} as const
export const MASTERY_THRESHOLDS = {
  strong: 75,
  solid: 55,
  developing: 35,
} as const
export type MasteryBandKey = keyof typeof MASTERY

/** Resolve a 0-100 mastery score to its band token. */
export function getMasteryBand(score: number): MasteryBandKey {
  if (score >= MASTERY_THRESHOLDS.strong) return 'strong'
  if (score >= MASTERY_THRESHOLDS.solid) return 'solid'
  if (score >= MASTERY_THRESHOLDS.developing) return 'developing'
  return 'attention'
}

// ─── Badge Variants ─────────────────────────────────────────────────
export const BADGE_VARIANTS = {
  default: 'bg-slate-100 text-slate-700 border-slate-200',
  primary: `bg-orange-100 text-orange-700 border-orange-200`,
  secondary: 'bg-amber-100 text-amber-700 border-amber-200',
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger: 'bg-rose-100 text-rose-700 border-rose-200',
  info: 'bg-blue-100 text-blue-700 border-blue-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  outline: 'bg-transparent text-slate-700 border-slate-300',
} as const

// ─── Section Accent (default for SectionHeader) ────────────────────
export const SECTION_ACCENT = {
  default: `bg-orange-100 text-orange-600`,
} as const

// ─── Radius scale (getokeniseerd — stopt rounded-[2rem]/[2.5rem]-drift) ─
// Sync met index.css: --radius-card (1.5rem) / --radius-button (1rem).
//   pill    = volledig rond (badges, chips)
//   button  = rounded-2xl (1rem)
//   card    = rounded-3xl (1.5rem)  ← default kaart-radius
//   surface = rounded-[2rem]        ← grote "wereld"/hero-vlakken
export const RADIUS = {
  pill: 'rounded-full',
  input: 'rounded-2xl',
  button: 'rounded-2xl',
  card: 'rounded-3xl',
  surface: 'rounded-[2rem]',
} as const

// ─── Shadow scale ───────────────────────────────────────────────────
export const SHADOW = {
  sm: 'shadow-sm',
  card: 'shadow-sm',
  elevated: 'shadow-lg',
  glow: 'shadow-orange-200/50',
} as const

// ─── Card ───────────────────────────────────────────────────────────
export const CARD = {
  base: `bg-white ${RADIUS.card} border border-slate-200 ${SHADOW.card}`,
  hoverBorder: 'rgb(254 215 170)', // orange-200 for framer-motion borderColor
} as const

// ─── Input (student variant) ────────────────────────────────────────
export const INPUT_STUDENT = {
  base: `border-2 border-slate-200 ${RADIUS.input} bg-slate-50/80 focus:bg-white text-slate-900 placeholder:text-slate-400`,
  focus: 'focus:ring-2 focus:ring-orange-100 focus:border-orange-400',
  error: 'border-red-300 focus:ring-red-500 focus:border-red-500',
} as const

// ─── Progress defaults ──────────────────────────────────────────────
export const PROGRESS = {
  barColor: 'bg-orange-500',
  ringColor: 'text-orange-500',
  trackColor: 'bg-slate-100',
} as const

// ─── Sidebar / Navigation active state ──────────────────────────────
export const NAV_ACTIVE_DEFAULT = {
  text: 'text-orange-600',
  bg: 'bg-orange-50',
  border: 'border-orange-200',
  icon: 'text-orange-600',
} as const
